import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import type { TEnumItem } from '@modules/types'
import { useMemo, useState } from 'react'
import { REQUESTS_LIST_COLUMNS_STRINGS } from './strings'

export const PUBLISHER_REQUEST_TYPE_ENUM = 'cart_item_type'
const PUBLISHER_ALLOWED_REQUEST_TYPES = new Set(['Lyric Usage', 'Publication'])

export type TPublisherRequestListTypeFilter = {
  enumName: typeof PUBLISHER_REQUEST_TYPE_ENUM
  typeId: number
} | null

type RequestTypeColumnHeaderProps = {
  enums: TEnumItem[]
  enumsLoading: boolean
  requestTypeFilter: TPublisherRequestListTypeFilter
  onRequestTypeFilterChange: (filter: TPublisherRequestListTypeFilter) => void
}

export const RequestTypeColumnHeader = ({
  enums,
  enumsLoading,
  requestTypeFilter,
  onRequestTypeFilterChange,
}: RequestTypeColumnHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const typeItems = useMemo(
    () =>
      enums
        .filter(({ enumName }) => enumName === PUBLISHER_REQUEST_TYPE_ENUM)
        .filter(item => PUBLISHER_ALLOWED_REQUEST_TYPES.has(item.name))
        .sort((a, b) => a.id - b.id),
    [enums],
  )

  const isFiltered = requestTypeFilter !== null

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          width: '100%',
          pr: 0.5,
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          {REQUESTS_LIST_COLUMNS_STRINGS.requestType}
        </Typography>
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          disabled={enumsLoading}
          aria-label={REQUESTS_LIST_COLUMNS_STRINGS.requestTypeFilterMenuAria}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            color: theme =>
              isFiltered ? theme.palette.primary.main : theme.palette.text.secondary,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            onRequestTypeFilterChange(null)
            setAnchorEl(null)
          }}
          selected={!requestTypeFilter}
        >
          {REQUESTS_LIST_COLUMNS_STRINGS.requestTypeFilterAll}
        </MenuItem>
        {typeItems.map(item => (
          <MenuItem
            key={`type-${item.id}`}
            onClick={() => {
              onRequestTypeFilterChange({
                enumName: PUBLISHER_REQUEST_TYPE_ENUM,
                typeId: item.id,
              })
              setAnchorEl(null)
            }}
            selected={requestTypeFilter?.typeId === item.id}
            sx={{ pl: 3 }}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export const requestTypeFilterToListFilter = (
  requestTypeFilter: TPublisherRequestListTypeFilter,
) => {
  if (!requestTypeFilter) return []
  return [
    {
      field: 'client_request.cart_item_type_id',
      operator: 'eq' as const,
      value: requestTypeFilter.typeId,
    },
  ]
}
